import BackgroundGeolocation, {
  type Location,
  type Subscription,
  type HttpEvent
} from "@transistorsoft/capacitor-background-geolocation"
import { App } from "@capacitor/app"
import type { PluginListenerHandle } from "@capacitor/core"
import { ref } from "vue"
import { Capacitor } from "@capacitor/core"
import { getToken } from "@clerk/vue"

// API URL from environment
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

export function useBackgroundLocation() {
  const lastLocation = ref<Location | null>(null)
  let locationSubscription: Subscription | null = null
  let httpSubscription: Subscription | null = null
  let heartbeatSubscription: Subscription | null = null
  let appStateListener: PluginListenerHandle | null = null

  /**
   * Get the current JWT token from Clerk
   */
  async function getCurrentToken(): Promise<string> {
    try {
      const token = await getToken({ template: "jwt" })
      return token || ""
    } catch (error) {
      console.error("Failed to get Clerk token:", error)
    }
    return ""
  }

  /**
   * Update the plugin's authorization header with the current token
   * Call this when the token changes (e.g., on session refresh)
   */
  async function updateAuthorizationToken(): Promise<void> {
    const token = await getCurrentToken()
    if (token) {
      // https://transistorsoft.github.io/capacitor-background-geolocation/classes/backgroundgeolocation.html#setconfig
      await BackgroundGeolocation.setConfig({
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      console.log("BackgroundGeolocation authorization token updated")
    }
  }

  /**
   * Start tracking the location
   * @returns void
   */
  async function trackLocation() {
    if (!Capacitor.isNativePlatform()) {
      return
    }
    console.log("Initializing background location tracking...")

    try {
      // Get the current token for authorization
      const token = await getCurrentToken()

      // Subscribe to location events (for local state updates)
      locationSubscription = BackgroundGeolocation.onLocation((location) => {
        lastLocation.value = location
      })

      // Subscribe to HTTP events to monitor sync status
      httpSubscription = BackgroundGeolocation.onHttp(async (event: HttpEvent) => {
        // TODO I removed the console spam, but remaining logic looks kind of fishy.
        await updateAuthorizationToken()

        if (!event.success) {
          // If we get a 401, try to refresh the token
          if (event.status === 401) {
            await updateAuthorizationToken()
          }
        }
      })

      // Subscribe to heartbeat events - request location periodically when stationary
      heartbeatSubscription = BackgroundGeolocation.onHeartbeat(() => {
        // Request a new location on each heartbeat
        BackgroundGeolocation.getCurrentPosition({
          samples: 1,
          persist: true
        })
          .then((location) => {
            lastLocation.value = location
          })
          .catch((error) => {
            console.error("[onHeartbeat] ❌ Failed to get location:", error)
          })
      })

      // Refresh token when app comes to foreground
      appStateListener = await App.addListener("appStateChange", async ({ isActive }) => {
        if (isActive) {
          console.log("App came to foreground, refreshing authorization token...")
          await updateAuthorizationToken()
        }
      })

      // https://transistorsoft.github.io/capacitor-background-geolocation/interfaces/config.html
      const state = await BackgroundGeolocation.ready({
        desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_LOW,
        heartbeatInterval: 60,
        debug: false,
        locationAuthorizationAlert: {
          titleWhenNotEnabled: "Locatietoegang is vereist",
          titleWhenOff: "Locatietoegang is uitgeschakeld",
          instructions:
            "Je locatie delen zorgt voor betere planning en tevreden klanten. Privacy eerst: tracking is alleen actief tijdens je dienst.",
          cancelButton: "Annuleren",
          settingsButton: "Instellingen openen"
        },
        backgroundPermissionRationale: {
          title: "{applicationName} toegang geven tot je locatie",
          message:
            "Om de planning goed te laten werken en klanten te informeren over hun bestelling, moet locatie delen op '{backgroundPermissionOptionLabel}' staan.",
          positiveAction: "Zet op '{backgroundPermissionOptionLabel}'",
          negativeAction: "Annuleren"
        },
        logLevel: BackgroundGeolocation.LOG_LEVEL_VERBOSE,
        stopOnTerminate: false, // Continue tracking when app is terminated
        startOnBoot: true, // Auto-start on device reboot
        allowIdenticalLocations: true,
        enableHeadless: true, // Enable headless mode for Android
        // HTTP Sync Configuration - Using POST (plugin doesn't support PATCH)
        // NOTE: Your backend endpoint /auth/current-user/location/ should accept POST
        url: `${API_URL}/auth/current-user/location/`,
        method: "POST",
        preventSuspend: true,
        autoSync: true, // Automatically upload locations to server
        autoSyncThreshold: 0, // Upload immediately (no threshold)
        batchSync: false, // Send each location individually
        maxRecordsToPersist: 1, // Keep max 1 locations in queue

        // Custom location template to match API format
        // The API expects: { latitude, longitude }

        // Authorization header with Bearer token
        headers: {
          Authorization: token ? `Bearer ${token}` : ""
        },

        // Android-specific config
        notification: {
          title: "Locatie wordt gedeeld",
          text: "Je locatie delen zorgt voor betere planning en tevreden klanten. Privacy eerst: tracking is alleen actief tijdens je dienst.",
          sticky: true // Notification persists until explicitly stopped
        },

        foregroundService: true, // Required for background tracking
        // Request "Always" permission for background location tracking
        locationAuthorizationRequest: "Always"
      })

      console.log("BackgroundGeolocation ready with state:", state.enabled)

      if (!state.enabled) {
        // Start tracking
        await BackgroundGeolocation.start()
        console.log("Background location tracking started successfully")
      } else {
        console.log("Background location tracking was already enabled")
      }
    } catch (error) {
      console.error("Failed to initialize background location tracking:", error)
    }
  }

  /**
   * Stop tracking the location
   * @returns void
   */
  async function stopLocationTracking() {
    if (!Capacitor.isNativePlatform()) {
      return
    }

    console.log("Attempting to stop location tracking...")

    // Remove subscriptions
    if (locationSubscription) {
      locationSubscription.remove()
      locationSubscription = null
      console.log("Location subscription removed")
    }

    if (httpSubscription) {
      httpSubscription.remove()
      httpSubscription = null
      console.log("HTTP subscription removed")
    }

    if (heartbeatSubscription) {
      heartbeatSubscription.remove()
      heartbeatSubscription = null
      console.log("Heartbeat subscription removed")
    }

    if (appStateListener) {
      await appStateListener.remove()
      appStateListener = null
      console.log("App state listener removed")
    }

    try {
      await BackgroundGeolocation.stop()
      console.log("Background location tracking stopped successfully")
    } catch (error) {
      console.error("Failed to stop location tracking:", error)
    }
  }

  /**
   * Manually trigger a sync of any pending locations
   * Useful after coming back online or after token refresh
   */
  async function syncLocations(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return

    try {
      // First update the token
      await updateAuthorizationToken()
      // Then trigger sync
      const locations = await BackgroundGeolocation.sync()
      console.log("Manual sync completed, uploaded", locations.length, "locations")
    } catch (error) {
      console.error("Manual sync failed:", error)
    }
  }

  /**
   * Get the count of pending locations waiting to be synced
   */
  async function getPendingCount(): Promise<number> {
    if (!Capacitor.isNativePlatform()) return 0

    try {
      const count = await BackgroundGeolocation.getCount()
      return count
    } catch (error) {
      console.error("Failed to get pending count:", error)
      return 0
    }
  }

  return {
    lastLocation,
    trackLocation,
    stopLocationTracking,
    updateAuthorizationToken,
    syncLocations,
    getPendingCount
  }
}
